import { useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useDispatchApp } from "../../lib/redux";
import { startLinkAnonymousAccount } from "../../actions/auth";
import {
  EMAIL_PLACEHOLDER,
  FIELD_EMAIL_REGISTERED,
  FIELD_EMAIL_VALID,
  FIELD_MIN_LENGTH,
  FIELD_PASSWORD_INSECURE,
  FIELD_PASSWORDS_NO_MATCH,
  FIELD_REQUIRED,
  PASSWORD_CONFIRMATION_PLACEHOLDER,
  PASSWORD_PLACEHOLDER,
  READY,
} from "../../i18n/keysTranslations";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import Button from "../Buttons/Button";
import { REGEX_EMAIL, REGEX_PASSWORD } from "../../utils/regex";
import TextField from "../Form/TextField";
import { useTranslationApp } from "../../lib/i18next";
import ModalBasicLayout from "./ModalBasicLayout";
import {
  MODAL_CREATE_ACCOUNT_TITLE,
  MODAL_CREATE_ACCOUNT_DESCRIPTION,
  BUTTON_CREATE_ACCOUNT,
} from "../../i18n/keysTranslations";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../utils/colors";
import { ALERT_ICON_TYPE_SUCCESS, BORDER_RADIUS } from "../../utils/constants";
import DialogActions from "@mui/material/DialogActions";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { SimpleAlert } from "../Alerts/Alerts";
import { startGetAdsUsers } from "../../actions/user";

const ModalCreateAccountFromAnonymous = ({ modalOpen, onCloseModal }) => {
  const {
    control,
    handleSubmit,
    formState,
    watch,
    setError: setErrorForm,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const errors = formState.errors;
  useFocusErrorForm(formState);
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const password = watch("password");

  const onChangeShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (formValues) => {
    setError(null);
    setIsFetching(true);

    // Validate if email is already in use
    try {
      const methods = await fetchSignInMethodsForEmail(auth, formValues.email);
      if (methods.length > 0) {
        setIsFetching(false);
        setErrorForm("email", {
          type: "manual",
          message: t(FIELD_EMAIL_REGISTERED),
        });
        return;
      }
    } catch (errorFetch) {
      // If the validation fails for some reason, continue and let backend handle it
      console.error(errorFetch);
    }

    const response = await dispatch(
      startLinkAnonymousAccount({
        email: formValues.email,
        password: formValues.password,
      })
    );

    if (!response.ok) {
      setError(response.message || "Error al crear la cuenta");
    } else {
      SimpleAlert({
        title: t(READY),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });

      setIsFetching(false);
      onCloseModal();
      await dispatch(
        startGetAdsUsers({
          onCallbackLoadingFinish: () => {},
        })
      );
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(MODAL_CREATE_ACCOUNT_TITLE)}
      description={t(MODAL_CREATE_ACCOUNT_DESCRIPTION)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: "fields.background",
              color: "background.paperContrastText",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              borderRadius: BORDER_RADIUS,
              boxShadow: BOX_SHADOW_FIELDS,
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <TextField
          name="email"
          variant="filled"
          control={control}
          label={t(EMAIL_PLACEHOLDER)}
          type="email"
          errors={errors}
          rules={{
            required: t(FIELD_REQUIRED),
            pattern: {
              value: REGEX_EMAIL,
              message: t(FIELD_EMAIL_VALID),
            },
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          name="password"
          variant="filled"
          control={control}
          label={t(PASSWORD_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          errors={errors}
          rules={{
            required: t(FIELD_REQUIRED),
            minLength: {
              value: 6,
              message: t(FIELD_MIN_LENGTH, { value: 6 }),
            },
            pattern: {
              value: REGEX_PASSWORD,
              message: t(FIELD_PASSWORD_INSECURE),
            },
          }}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={onChangeShowPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          name="confirmPassword"
          variant="filled"
          control={control}
          label={t(PASSWORD_CONFIRMATION_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          errors={errors}
          rules={{
            required: t(FIELD_REQUIRED),
            validate: (value) =>
              value === password || t(FIELD_PASSWORDS_NO_MATCH),
          }}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle confirm password visibility"
                onClick={onChangeShowPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          sx={{ mb: 3 }}
        />

        <DialogActions sx={{ px: 0, pb: 0 }}>
          <Button
            type="submit"
            fullWidth
            loading={isFetching}
            disabled={isFetching}
          >
            {t(BUTTON_CREATE_ACCOUNT)}
          </Button>
        </DialogActions>
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalCreateAccountFromAnonymous;
